// The Direction panel (was the guiding-policy card). 056-vision-capture-
// method B2 (spec §6 output oracle "guiding-policy statement"), RE-HOUSED by
// the owner-approved redesign, 2026-07-09 (specs/056-vision-capture-method/
// redesign-brief.md), and RE-MAPPED by the interview design (owner-agreed
// interview design, 2026-07-09; specs/056-vision-capture-method/
// interview-design.md): the panel holds gate two's BOTH artifacts, the
// vision.guiding-policy singleton and the vision.north-star sentence. The
// north star gets the quote treatment beside the policy (its statement in
// the goal-name typography, its measure beneath as "Proof: <measure>"), and
// is confirmable like any fact. The panel appears when EITHER fact exists
// (a fact on the surface is never orphaned); the act completes when both
// confirm, and the collapsed strip quotes the NORTH STAR (the destination),
// falling back to the policy for sessions without one. The card grammar
// behind the door is UNCHANGED (in-place edit, lifecycle tag, one-click
// Confirm).

import React, { useEffect, useState } from 'react';
import { Check, Pencil, Trash2 } from 'lucide-react';
import type { GatedFact, SurfaceEdit, SurfaceFocus } from '../../facilitation/surfaceBinding';
import {
  DisputeComposer,
  DisputedBlock,
  FactCard,
  LifecycleTag,
  textOf,
  type EmitResult,
} from './factCardKit';
import VisionPanel from './VisionPanel';

function statementOf(northStar: GatedFact): string {
  if (typeof northStar.content.text === 'string' && northStar.content.text.trim()) {
    return northStar.content.text;
  }
  return typeof northStar.content.statement === 'string' ? northStar.content.statement : '';
}

// The north-star card (owner-agreed interview design, 2026-07-09): the
// destination sentence quoted in the goal-name typography, the proof line
// beneath, the lifecycle tag and a one-click Confirm. Nothing on it names
// its machinery.
const NorthStarCard: React.FC<{
  northStar: GatedFact;
  sealed: boolean;
  frozen: boolean;
  pulsing: boolean;
  registerRef: (factId: string, el: HTMLElement | null) => void;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
}> = ({ northStar, sealed, frozen, pulsing, registerRef, onEmitEdit }) => {
  const busy = northStar.provisionalLocal === true;
  const measure =
    typeof northStar.content.measure === 'string' && northStar.content.measure.length > 0
      ? northStar.content.measure
      : null;
  const confirmable =
    northStar.lifecycle !== 'confirmed' && northStar.lifecycle !== 'disputed';
  const className = [
    'vsf-card',
    `vsf-card--${northStar.lifecycle}`,
    'vsf-northstar',
    pulsing ? 'vsf-pulse' : '',
    sealed ? 'vsf-card--sealed' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const [editing, setEditing] = useState(false);
  const [draftStatement, setDraftStatement] = useState(statementOf(northStar));
  const [draftMeasure, setDraftMeasure] = useState(measure || '');
  useEffect(() => {
    setDraftStatement(statementOf(northStar));
    setDraftMeasure(measure || '');
  }, [northStar, measure]);
  return (
    <div
      className={className}
      data-fact-id="vision.north-star"
      tabIndex={-1}
      ref={(el) => registerRef('vision.north-star', el)}
    >
      {northStar.lifecycle === 'disputed' ? (
        <DisputedBlock
          fact={northStar}
          noun="north star"
          disabled={busy || frozen}
          readOnly={sealed}
          onEmitEdit={onEmitEdit}
        />
      ) : editing && !sealed ? (
        <div className="vsf-singleton-editor">
          <input
            className="vsf-field"
            aria-label="North-star statement"
            value={draftStatement}
            onChange={(event) => setDraftStatement(event.target.value)}
          />
          <input
            className="vsf-field"
            aria-label="North-star measure"
            placeholder="How will we know?"
            value={draftMeasure}
            onChange={(event) => setDraftMeasure(event.target.value)}
          />
          <button type="button" className="vsf-btn" onClick={() => setEditing(false)}>Cancel</button>
          <button
            type="button"
            className="vsf-btn"
            disabled={!draftStatement.trim()}
            onClick={() => {
              setEditing(false);
              onEmitEdit({
                capabilityId: 'vision.set_north_star',
                input: {
                  statement: draftStatement.trim(),
                  ...(draftMeasure.trim() ? { measure: draftMeasure.trim() } : {}),
                },
              });
            }}
          >
            Save
          </button>
        </div>
      ) : (
        <>
          <p className="vsf-ns-statement">“{statementOf(northStar)}”</p>
          {measure && <p className="vsf-mut vsf-ns-proof">Proof: {measure}</p>}
        </>
      )}
      <div className="vsf-card-foot">
        <LifecycleTag fact={northStar} />
        {!sealed && confirmable && (
          <button
            type="button"
            className="vsf-btn vsf-confirm"
            disabled={busy || frozen}
            onClick={() =>
              onEmitEdit({
                capabilityId: 'vision.confirm_fact',
                input: { factId: 'vision.north-star' },
              })
            }
          >
            <Check size={12} aria-hidden="true" /> Confirm
          </button>
        )}
        {!sealed && northStar.lifecycle !== 'disputed' && (
          <>
            <button
              type="button"
              className="vsf-btn"
              disabled={busy || frozen}
              onClick={() => setEditing(true)}
            >
              <Pencil size={12} aria-hidden="true" /> Edit
            </button>
            <DisputeComposer
              fact={northStar}
              noun="north star"
              disabled={busy || frozen}
              onEmitEdit={onEmitEdit}
            />
          </>
        )}
        {!sealed && (
          <button
            type="button"
            className="vsf-reject"
            aria-label="Reject north star"
            disabled={busy || frozen}
            onClick={() => onEmitEdit({
              capabilityId: 'vision.reject_fact',
              input: { factId: northStar.factId },
            })}
          >
            <Trash2 size={13} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export interface GuidingPolicyCardProps {
  policy?: GatedFact;
  northStar?: GatedFact;
  /** Resolved texts of the challenges the policy answers (may be empty). */
  answersTexts: string[];
  sealed: boolean;
  busy: boolean;
  pulsing: boolean;
  northStarPulsing?: boolean;
  /** Derived by the surface from the active act (owner-approved redesign,
   * 2026-07-09): Direction collapses to the quoted strip once What it is
   * worth is the active act. */
  defaultExpanded: boolean;
  /** The active act carries the head tint. */
  active: boolean;
  registerRef: (factId: string, el: HTMLElement | null) => void;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
  onPublishFocus?: (focus: SurfaceFocus) => void;
}

const GuidingPolicyCard: React.FC<GuidingPolicyCardProps> = ({
  policy,
  northStar,
  answersTexts,
  sealed,
  busy,
  pulsing,
  northStarPulsing = false,
  defaultExpanded,
  active,
  registerRef,
  onEmitEdit,
  onPublishFocus,
}) => {
  // The strip quotes the destination: the north star when it stands, the
  // policy otherwise (owner-agreed interview design, 2026-07-09).
  const stripQuote = northStar ? statementOf(northStar) : policy ? textOf(policy) : '';
  const done =
    policy?.lifecycle === 'confirmed' && northStar?.lifecycle === 'confirmed';
  return (
    <VisionPanel
      panelId="vsf-policy"
      testId="vsf-policy"
      title="Direction"
      subLine="Direction, not a measure. Measures live in Benefits below."
      sealed={sealed}
      active={active}
      defaultExpanded={defaultExpanded}
      strip={{
        label: 'Direction',
        done,
        summary: <span className="vpanel-strip-quote">“{stripQuote}”</span>,
      }}
    >
      {policy && (
        <div className={policy.lifecycle === 'confirmed' ? 'vsf-policy-quote' : undefined}>
          <FactCard
            fact={policy}
            noun="direction"
            sealed={sealed}
            frozen={busy}
            pulsing={pulsing}
            detail={
              answersTexts.length > 0 ? (
                <div className="vsf-mut">Answers: {answersTexts.join(' · ')}</div>
              ) : undefined
            }
            registerRef={registerRef}
            onEmitEdit={onEmitEdit}
            onPublishFocus={onPublishFocus}
          />
        </div>
      )}
      {northStar && (
        <NorthStarCard
          northStar={northStar}
          sealed={sealed}
          frozen={busy}
          pulsing={northStarPulsing}
          registerRef={registerRef}
          onEmitEdit={onEmitEdit}
        />
      )}
    </VisionPanel>
  );
};

export default GuidingPolicyCard;
